import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { Alert } from "@mui/material";
import axios from "../axios";

import "./dashboard.css";
import { personalData } from "../states/personalData";
import { allCourses } from "../states/allCourses";
import { alertData } from "../states/alert";
import { dashboardCardsData } from "../states/dashboardCardsData";
import StudentCard from "../components/student-card";
import { logEvent, logPageView } from "../analytics";
import TutorDashboardHeaderBanner from "./../components/TutorDashboardHeaderBanners/TutorDashboardHeaderBanner";
import SubmitBanner from "../components/SubmitBanner/SubmitBanner";
import VideoModal from "../components/VideoModal/VideoModal";

const currentDate = new Date();
const currentHour = currentDate.getHours();

export default function Dashboard() {
  const [fullCourseTemplate, setFullCourseTemplate] = useAtom(allCourses);
  const [cardsCenteralData, setCardsCenteralData] = useAtom(dashboardCardsData);
  const [personalCenteralData, setPersonalCenteralData] = useAtom(personalData);
  const [subNavigation, setSubNavigation] = useState("RECOMMENDATION");

  const navigation = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useAtom(alertData);
  const [sortOption, setSortOption] = useState("");
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const verifyPaymentDetails = async (userId, data) => {
    try {
      await axios.post(`/v1/user/verify-payment`, {
        userId: userId,
      });
      let updatedData = {
        id: data.id,
        name: data.first_name,
        l_name: data.last_name,
        gender: data.gender,
        email: data.email,
        phone: data.phone,
        phone2: data.phone2 === "null" ? "" : data.phone2,
        latitude: data.latitude,
        longitude: data.longitude,
        flat: data.flat === "undefined" ? "" : data.flat,
        apartment: data.apartment === "undefined" ? "" : data.apartment,
        street: data.street === "undefined" ? "" : data.street,
        locality: data.locality === "undefined" ? "" : data.locality,
        pincode: data.zip,
        city: data.city_name,
        state: data.state_name,
        tutor_verified: data.is_active, //uncommented this because we need this
        profile_pic_link: data.profile_pic_link ? data.profile_pic_link : "./person-icon.png",
        profile_status: data.profile_status,
      };
      // // console.log(updatedData);
      // setSubNavigation((data.is_active)?'DEMO_CLASSES':'RECOMMENDATION')
      localStorage.setItem("userDetails", JSON.stringify(updatedData));
      setPersonalCenteralData((pre) => ({ ...pre, ...updatedData }));
      setLoading(false);
      axios
        .post(`v1/tutor/tutor-doc-status`)
        .then((res) => {
          const {
            is_approved,
            adhaar,
            id_card,
            is_paid,
            advance_amount,
            cv_file,
            resume_file,
            intro_video,
            must_accept_terms,
            terms_accepted_at,
          } = res.data.status;
          setPersonalCenteralData((pre) => ({
            ...pre,
            tutor_verified: is_paid,
            adhaar,
            id_card,
            is_paid,
            advance_amount,
            cv_file,
            resume_file,
            intro_video,
            must_accept_terms,
            terms_accepted_at,
          }));
          setLoading(false);
        })
        .catch((error) => {
          setLoading(true);
          // console.log(error);
        });
    } catch (error) {
      console.log(error);
    }
  };

  const getPersonalDetails = () => {
    axios
      .get("/v1/user")
      .then((response) => {
        if (response.data.is_success) {
          const data = { ...response.data.data };
          verifyPaymentDetails(data.id, data);
        } else {
          setLoading(true);
          throw response.data.error;
        }
      })
      .catch((err) => {
        setLoading(true);
        // console.log(err);
      });
  };

  const timeOfDay = () => {
    let timeOfDay;

    if (currentHour >= 5 && currentHour < 12) {
      timeOfDay = "Morning";
    } else if (currentHour >= 12 && currentHour < 18) {
      timeOfDay = "Afternoon";
    } else {
      timeOfDay = "Evening";
    }
    return timeOfDay;
  };

  const getAppliedJobs = () => {
    axios
      .get("/v1/tutor/applied-jobs")
      .then((response) => {
        if (response.data.is_success) {
          const AppliedJobIds = response.data.data.map((id) => parseInt(id)); // normalize to number
          setCardsCenteralData((currentData) =>
            currentData.map((card) => {
              if (AppliedJobIds.includes(parseInt(card.dealings_id))) {
                return { ...card, isActive: true };
              } else {
                return card;
              }
            }),
          );
        }
      })
      .catch((err) => {
        setLoading(true);
      });
  };

  const getSubNavCount = (domain) =>
    cardsCenteralData.filter((item) => {
      return item.card_domain === domain;
    }).length;

  const SubNavigationLink = ({ domain, text }) => (
    <div
      onClick={() => {
        fetchDashboardCards(domain); // 🔁 Refetch cards on tab click
        setSubNavigation(domain);
      }}
      className={`${subNavigation === domain ? "active-sub-link" : "unactive-sub-link"}`}
    >
      {text}
      {getSubNavCount(domain) !== 0 && !loading && (
        <span className="sub-nav-notification">{getSubNavCount(domain)}</span>
      )}
    </div>
  );

  const fillCourseTemplate = () => {
    axios
      .get("/v1/tutor/all-courses")
      .then((response) => {
        let fullCourseTemplate = response.data.data;
        setFullCourseTemplate(fullCourseTemplate);
        if (fullCourseTemplate.filter((dropdownForm) => dropdownForm.selected_courses.length !== 0).length === 0)
          navigation("/my-courses");
        setLoading(false);
      })
      .catch((err) => {
        setLoading(true);
      });
  };

  useEffect(() => {
    logPageView("tutor-dashboard-page");
    const jwt = JSON.parse(localStorage.getItem("homeshiksha-tutor-jwt"));
    if (!jwt) {
      navigation("/");
      window.location.reload();
      return;
    }

    if (localStorage.getItem("dynamic")) {
      navigation("/update/address-and-course");
      return;
    }

    // to get personal info
    if (Object.keys(personalCenteralData).length === 0) {
      getPersonalDetails();
    }

    //  to get all the couses from DB
    if (fullCourseTemplate.length === 0) {
      fillCourseTemplate();
    } else {
      //  to get tutor selected courses only
      setLoading(false);
      const resultCourseFroms = fullCourseTemplate.filter((dropdownForm) => dropdownForm.selected_courses.length !== 0);
      if (resultCourseFroms.length === 0) navigation("/my-courses");
    }

    // to get all the session details and recomended session details
    console.log("🚀 ~ cardsCenteralData:", cardsCenteralData.length);

    setLoading(true);
    axios
      .get("/v1/tutor/dashboard-cards-new?card_domain=RECOMMENDATION")
      .then(async (response) => {
        const recommendationCards = response.data.data;
        setCardsCenteralData(recommendationCards);

        getAppliedJobs();
        setLoading(false);
      })
      .catch((err) => {
        setLoading(true);
      });
  }, []);

  const fetchDashboardCards = (card_domain = "RECOMMENDATION", sortOption = "distance_nearest") => {
    setLoading(true);
    setSortOption(sortOption);
    axios
      .get(`/v1/tutor/dashboard-cards-new?card_domain=${card_domain}&sortBy=${sortOption}`)
      .then((response) => {
        const tutorCards = response.data.data;
        setCardsCenteralData(tutorCards);
        getAppliedJobs();
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  return (
    <main className="main">
      {
        !personalCenteralData.tutor_verified && !personalCenteralData.is_paid && (
          <section>
            <TutorDashboardHeaderBanner></TutorDashboardHeaderBanner>
          </section>
        )
      }

      <section style={{ padding: "0 16px" }}>
        <SubmitBanner />
      </section>
      {!personalCenteralData.tutor_verified && !personalCenteralData.is_paid && (
        <Link to="#" onClick={(e) => { e.preventDefault(); setIsVideoModalOpen(true); }} className="video-text-link">
          <span className="video-text-style">What tutors say about HomeShiksha <img src="./video-icon.svg" alt="video" height={20} width={20} /></span>
        </Link>
      )}
      <section
        className={`greating-section-wrrapper ${personalCenteralData.tutor_verified && personalCenteralData.is_paid
          ? "greating-section-wrrapper-verified"
          : "greating-section-wrrapper-unverified"
          }`}
      >
        <div
          className={`greating-section--profile--wrapper ${personalCenteralData.tutor_verified && personalCenteralData.is_paid
            ? "greating-section--profile--wrapper-verified"
            : "greating-section--profile--wrapper-unverified"
            }`}
        >
          <img src={personalCenteralData.profile_pic_link} alt="user profile" width="100%" />
        </div>

        <div className="greating-section--profile-content-wrapper">
          <div className="greatings-title">
            Good {timeOfDay()} {personalCenteralData.name} !
          </div>
          <div className="greatings-desc">
            {!personalCenteralData.tutor_verified && !personalCenteralData.is_paid
              ? "You are Unverified Tutor"
              : "You Are Verified Tutor"}{" "}
            {personalCenteralData.profile_status === "Warning" && (
              <span className="profile-status-warning">Warning</span>
            )}
            {personalCenteralData.profile_status === "Block" && (
              <span className="profile-status-blocked">Blocked</span>
            )}
          </div>

          {!!personalCenteralData.tutor_verified && !personalCenteralData.is_paid && (
            <img
              className="verified-tutor-status-icon"
              src="./verified-tutor-status-icon.svg"
              alt="verified-tutor-status-icon"
              width="15px"
            />
          )}
        </div>
      </section>

      {alert && (
        <Alert
          severity="success"
          onClose={() => {
            setAlert(false);
          }}
        >
          {alert}
        </Alert>
      )}

      {!personalCenteralData.tutor_verified && !personalCenteralData.is_paid && (
        <section className="verify-now-section--wrapper">
          <div className="verify-now-section--left--wrapper">
            <div
              className="verify-now-section--left--warning-icon-wrapper"
              style={{ backgroundImage: 'url("./warning-icon.svg")' }}
            >
              !{/* <img src="./warning-icon.svg" alt="warning" /> */}
            </div>
            <div>Verify your account to get started with classes</div>
          </div>

          <div className="verify-now-section--right--wrapper">
            <NavLink to="/verify-account">
              <div className="verify-now-section--right--wrapper-text">Verify Now</div>
            </NavLink>
          </div>
        </section>
      )}

      <section className="subnavigation-section-wrapper">
        <div className="subnavigation-section--titles--wrapper">
          <div className="my-tabs">
            <SubNavigationLink domain="RECOMMENDATION" text="Recommended" />
            <SubNavigationLink domain="DEMO_CLASSES" text="Demo Classes" />
            <SubNavigationLink domain="MY_CLASSES" text="My Classes" />
          </div>

          {/* ✅ Sort Dropdown */}
          <div className="sort-by-dropdown">
            <label htmlFor="sortField" className="sort-by-label">
              Sort By:
            </label>
            <select
              id="sortField"
              className="sort-by-select"
              value={sortOption}
              onChange={(e) => fetchDashboardCards(subNavigation, e.target.value)}
              style={{ color: "rgba(154, 154, 154, 1)" }}
            >
              <option value="">Selected Field</option>
              <option style={{ color: "rgba(91, 91, 91, 1)" }} value="distance_nearest">
                Distance: Nearest
              </option>
              <option style={{ color: "rgba(91, 91, 91, 1)" }} value="distance_far">
                Distance: Far
              </option>
              <option style={{ color: "rgba(91, 91, 91, 1)" }} value="fees_high_to_low">
                Fees: High to Low
              </option>
              <option style={{ color: "rgba(91, 91, 91, 1)" }} value="fees_low_to_high">
                Fees: Low to High
              </option>
            </select>
          </div>
        </div>

        {/* ✅ Sorting logic applied below */}
        {!loading ? (
          <div className="subnavigation-section-content--wrapper-tutor">
            <div
              className={`subnavigation-section-left-content--wrapper-tutor ${personalCenteralData.tutor_verified
                ? "subnavigation-section-left-content--wrapper-tutor-verified"
                : "subnavigation-section-left-content--wrapper-tutor-unverified"
                }`}
            >
              {(() => {
                // Separate hot leads and regular cards
                const sortedHotLeads = cardsCenteralData.filter((card) => card.is_hotlead);
                const sortedOthers = cardsCenteralData.filter((card) => !card.is_hotlead);

                return (
                  <>
                    {/* 🔥 Hot Leads Container */}
                    {sortedHotLeads.length > 0 && (
                      <div className="hotlead-cards-container">
                        {sortedHotLeads.map((itm, indx) => (
                          <div className="student-card" key={`hot-${indx}`}>
                            <StudentCard
                              cardId={itm.dealings_id}
                              distance={itm.distance}
                              amount={itm.amount}
                              is_hotlead={itm.is_hotlead}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 🧾 Normal Leads Container */}
                    {sortedOthers.length > 0 ? (
                      <div className="normal-cards-container">
                        {sortedOthers.map((itm, indx) => (
                          <div className="student-card" key={`normal-${indx}`}>
                            <StudentCard
                              cardId={itm.dealings_id}
                              distance={itm.distance}
                              amount={itm.amount}
                              is_hotlead={itm.is_hotlead}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      sortedHotLeads?.length <= 0 ? (
                        <div>
                          {subNavigation === "RECOMMENDATION"
                            ? "No Deal is Assigned Or Match to your courses"
                            : subNavigation === "DEMO_CLASSES"
                              ? "No Demo Classes Assigned"
                              : subNavigation === "MY_CLASSES"
                                ? "No Confirmed Classes Yet"
                                : "No Classes Assigned"}
                        </div>
                      ) : <></>)}
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80vh",
            }}
          >
            <div className="loading">
              <img src="./book-loader.gif" alt=" Loading . . .  " />
            </div>
          </div>
        )}
      </section>
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="SqsVjtqgtSk"
        title="What tutors say about HomeShiksha"
      />
    </main>
  );
}
